import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import {
  AssetRoutine,
  AssetRoutineState,
  FIND_ROUTINE_DRAFT,
  Issue,
  RoutineConfig,
  RoutineWorkflow,
  Severity,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomQuery,
  useFindByQuery,
  WorkspaceAsset,
  WorkspaceRoutine,
  WorkspaceType,
} from '@gripp/shared-logic';
import { extractAndDeduplicateRoutines } from 'libs/shared-ui/src/utils/assetHelpers';
import { uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { Colors } from '../../../themes/colors';
import LoadingIndicator from '../../loadingIndicator';
import { RoutineModule } from '../types';
import { EditRequestRoutine } from './editRoutine';
import { RoutineModuleButton } from './routineModuleButton';

type RoutineButtonsProps = {
  workspaceAsset: WorkspaceAsset;
  showAll: boolean;
  initialRoutinesToShow: number;
  hideAssignRoutinesButton?: boolean;
  onRoutineSelected: (props: RoutineExecutionProps) => void;
  buttonStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  buttonIcon?: Severity;
  buttonIconStyle?: StyleProp<ViewStyle>;
  buttonsContainerStyle?: StyleProp<ViewStyle>;
};

export type RoutineExecutionProps = {
  workspaceAsset: WorkspaceAsset;
  module: RoutineModule<any, any>;
  routine?: WorkspaceRoutine;
  state?: AssetRoutineState<any>;
  additionalState?: any;
  sourceIssue?: Issue;
  workflows?: RoutineWorkflow[];
  isIssueEditRoutine?: boolean;
  assetRoutine?: AssetRoutine;
  onCanceled?: () => void;
  onCompleted?: () => void;
  autoPromptQueue?: RoutineExecutionProps[];
};

export const RoutineButtonsOld = (props: RoutineButtonsProps) => {
  const { workspaceAsset, showAll, initialRoutinesToShow } = props;
  const currentWorkspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;
  const isWeb = Platform.OS === 'web';

  const userRoleIds = useMemo(
    () => account?.roles?.map((r) => r.id) || [],
    [account?.roles]
  );

  const isRoutinePermitted = useCallback(
    (routine: WorkspaceRoutine) => {
      const isGlobalAdmin = account?.isGlobalAdmin();
      const permittedRoles = (routine.config as any)?.permittedRoles;

      if (isGlobalAdmin) return true;
      if (!permittedRoles || permittedRoles.length === 0) return true;
      return permittedRoles.some((roleId: string) =>
        userRoleIds.includes(roleId)
      );
    },
    [account, userRoleIds]
  );

  const orFilters: any[] = [
    { workspaceAsset: { eq: workspaceAsset.id } },
    { group: { in: workspaceAsset.groups?.map((g) => g.id) || [] } },
  ];

  if (isOEM && workspaceAsset.asset?.product?.id) {
    orFilters.push({ product: { eq: workspaceAsset.asset.product.id } });
  }

  const { data: workflowData, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: {
      filter: {
        or: orFilters,
        and: {
          disabled: {
            ne: true,
          },
        },
      },
    },
  });

  const isOneTime = (routine: WorkspaceRoutine) =>
    !!(routine.config as any)?.oneTimeOnly;

  const hasState = useCallback(
    (routine: WorkspaceRoutine, asset: WorkspaceAsset) =>
      workspaceAsset.routineState?.some(
        (s) => s.workspaceRoutineId === routine.id
      ),
    [workspaceAsset.routineState]
  );

  const buttonData = useMemo(() => {
    const allExternalRoutines: WorkspaceRoutine[] =
      extractAndDeduplicateRoutines(workspaceAsset);
    const workspaceRoutines = workspaceAsset.routines || [];

    if (!workspaceRoutines.length && !allExternalRoutines.length) {
      return [];
    }

    // Filter routines based on user permissions
    const filterByPermissions = (routines: WorkspaceRoutine[]) =>
      routines.filter(isRoutinePermitted);

    // Filter routines for one-time behavior: show pending one-time routines, hide completed ones
    const filterOneTimeRoutines = (routines: WorkspaceRoutine[]) => {
      const active = routines.filter((r) => !r.deactivated);
      const pendingOneTime = active.filter(
        (r) => isOneTime(r) && !hasState(r, workspaceAsset)
      );
      // If there are pending one-time routines, show only those. Otherwise, hide completed one-time routines
      return pendingOneTime.length > 0
        ? pendingOneTime
        : active.filter((r) => !isOneTime(r) || !hasState(r, workspaceAsset));
    };

    // Get order for a routine, checking asset-level routineOrder first, then product's routineOrder, then falling back to inherent order
    const getRoutineOrder = (routine: WorkspaceRoutine) => {
      const assetOrder = workspaceAsset.routineOrder?.find(
        (x) => x.workspaceRoutineId === routine.id
      );
      if (assetOrder) return assetOrder.order;

      const productOrder = (
        workspaceAsset.asset?.product as any
      )?.routineOrder?.find((x: any) => x.workspaceRoutineId === routine.id);
      if (productOrder) return productOrder.order;

      return routine.order;
    };

    // Sort routines and map to button data format
    const toButtonData = (routines: WorkspaceRoutine[]) =>
      routines
        .sort((a, b) => getRoutineOrder(a) - getRoutineOrder(b))
        .map((routine) => ({
          routine,
          state: workspaceAsset.routineState?.find(
            (s) => s.workspaceRoutineId === routine.id
          ),
        }));

    const permittedExternal = filterByPermissions(allExternalRoutines);
    const permittedWorkspace = filterByPermissions(workspaceRoutines);

    // Combine all permitted routines, deduplicate, then apply one-time logic to the full pool
    const allPermitted = uniqBy(
      [...permittedExternal, ...permittedWorkspace],
      'id'
    );
    const visibleRoutines = filterOneTimeRoutines(allPermitted);

    const combineRoutines = uniqBy(toButtonData(visibleRoutines), 'routine.id');

    if (combineRoutines.length > initialRoutinesToShow && !showAll) {
      return combineRoutines.slice(0, initialRoutinesToShow);
    }

    return combineRoutines;
  }, [
    workspaceAsset,
    showAll,
    initialRoutinesToShow,
    hasState,
    isRoutinePermitted,
  ]);

  const getRoutineWorkflows = useCallback(
    (id: string, config: RoutineConfig, state: any) => {
      if (!id || !workflowData?.items?.length) return [];

      return workflowData.items
        .filter(
          (item: RoutineWorkflow) =>
            item.trigger?.config?.workspaceRoutineId === id
        )
        .map((workflow: RoutineWorkflow) => {
          return { ...workflow, uom: config?.uom, count: state?.count };
        });
    },
    [workflowData]
  );

  const onClick = (
    routine: WorkspaceRoutine,
    module: RoutineModule<any, any>,
    state?: AssetRoutineState<any>,
    additionalState?: any,
    workflows?: RoutineWorkflow[]
  ) => {
    props.onRoutineSelected({
      workspaceAsset,
      routine,
      module,
      state,
      additionalState,
      workflows,
    });
  };

  const Buttons = () => {
    if (buttonData?.length === 0) return null;

    return buttonData.map((data: any, index: number) => {
      const workspaceRoutineId = data.routine?.id;

      const config = data.routine?.config;
      const state = data.state?.state;

      const workflows = getRoutineWorkflows(
        workspaceRoutineId ?? '',
        config,
        state
      );

      const hasWorkflows = workflows.length > 0;

      const alertIconStyle = props.buttonStyle
        ? [styles.alertIconBase, styles.alertIcon]
        : styles.alertIconBase;

      // Check for draft
      const DraftIndicator = ({ routineId }: { routineId: string }) => {
        const { data: draftData } = useCustomQuery({
          cacheKey: ['routineDraft', workspaceAsset?.id, routineId],
          query: FIND_ROUTINE_DRAFT,
          variables: {
            workspaceAssetId: workspaceAsset?.id || '',
            routineId: routineId,
          },
          options: {
            enabled: !!(workspaceAsset?.id && routineId),
          },
        });

        const hasDraft = draftData?.routine?.findRoutineDraft;

        if (!hasDraft) return null;

        return (
          <MaterialCommunityIcons name="pencil" style={styles.draftIcon} />
        );
      };

      const HasWorkflows = hasWorkflows && (
        <MaterialCommunityIcons name="alarm-check" style={alertIconStyle} />
      );

      const secondaryButtonIcon = isWeb ? (
        <>
          {HasWorkflows}
          <DraftIndicator routineId={workspaceRoutineId || ''} />
        </>
      ) : (
        <>{HasWorkflows}</>
      );
      return (
        <RoutineModuleButton
          key={`routine-button-${index}`}
          routine={data.routine}
          state={data.state as AssetRoutineState<any>}
          buttonIndex={index}
          buttonCount={buttonData.length}
          buttonStyle={props.buttonStyle}
          buttonIcon={props.buttonIcon}
          buttonIconStyle={props.buttonIconStyle}
          labelStyle={[props.labelStyle, { flexShrink: 1 }]}
          onClick={onClick}
          leftButtonIcon={
            !isWeb && <DraftIndicator routineId={workspaceRoutineId || ''} />
          }
          secondaryButtonIcon={secondaryButtonIcon}
          workflows={workflows}
        />
      );
    });
  };

  return (
    <ScrollView
      scrollEnabled={showAll}
      showsVerticalScrollIndicator={false}
      style={
        props.buttonsContainerStyle
          ? props.buttonsContainerStyle
          : styles.scrollView
      }
    >
      {isLoading ? <LoadingIndicator flexContainer /> : <Buttons />}
      {(buttonData.length < initialRoutinesToShow || showAll) &&
        !props.hideAssignRoutinesButton && (
          <View style={buttonData.length > 1 && { marginTop: 16 }}>
            <EditRequestRoutine workspaceAsset={workspaceAsset} />
          </View>
        )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 0,
  },
  alertIconBase: {
    fontSize: 22,
    color: Colors.white,
  },
  alertIcon: {
    ...Platform.select({
      web: {
        color: Colors.primaryDark,
      },
    }),
  },
  draftIcon: {
    fontSize: 20,
    color: Colors.white,
    ...Platform.select({
      web: {
        color: Colors.primaryDark,
      },
    }),
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
      }
    }
  }
`;
